import { formatSmartField } from 'src/components/lead-sidebar/format-smart-field';

export type DisplayedSmartField = {
    key: string;
    label: string;
    value: string;
};

/* Numeric value of a raw money string (e.g. "5,000" or "$5,000"), or undefined if it
   isn't a finite number. */
function parseMoney(raw: string): number | undefined {
    const n = Number(raw.replace(/[,$\s]/g, ''));
    return raw.trim() !== '' && Number.isFinite(n) ? n : undefined;
}

/* Comma-grouped dollar amount (e.g. $5,000), matching how property rent is shown. */
export function formatMoney(raw: string): string {
    const n = parseMoney(raw);
    return n === undefined ? raw : `$${n.toLocaleString()}`;
}

/* Income shown as a single entry: annual then monthly, each suffixed with its cadence
   ($60,000/yr | $5,000/mo), joined by '|'. When only one figure is known and numeric,
   the other is derived (annual = monthly × 12). Returns '' when neither is present. */
export function formatIncome(monthly: string | undefined, annual: string | undefined): string {
    const monthlyNum = monthly !== undefined ? parseMoney(monthly) : undefined;
    const annualNum = annual !== undefined ? parseMoney(annual) : undefined;

    const showMonthly = monthly ?? (annualNum !== undefined ? String(Math.round(annualNum / 12)) : undefined);
    const showAnnual = annual ?? (monthlyNum !== undefined ? String(monthlyNum * 12) : undefined);

    const parts: string[] = [];
    if (showAnnual !== undefined) {
        parts.push(`${formatMoney(showAnnual)}/yr`);
    }
    if (showMonthly !== undefined) {
        parts.push(`${formatMoney(showMonthly)}/mo`);
    }
    return parts.join(' | ');
}

export type SmartFieldSplit = {
    monthlyIncome?: string;
    annualIncome?: string;
    hasIncome: boolean;
    /** The non-income string fields, sorted by display label. */
    otherFields: [string, string][];
};

/* Split entries into the income pair and the remaining string fields (sorted by label). */
export function splitSmartFields(
    validSmartFields: [string, string | Record<string, string>][]
): SmartFieldSplit {
    const stringFields = validSmartFields.filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string'
    );
    const monthlyIncome = stringFields.find(([key]) => key === 'monthly_income')?.[1];
    const annualIncome = stringFields.find(([key]) => key === 'annual_income')?.[1];
    const otherFields = stringFields
        .filter(([key]) => key !== 'monthly_income' && key !== 'annual_income')
        .sort(([keyA], [keyB]) =>
            formatSmartField(keyA, '').fieldLabel.localeCompare(formatSmartField(keyB, '').fieldLabel)
        );
    return {
        monthlyIncome,
        annualIncome,
        hasIncome: monthlyIncome !== undefined || annualIncome !== undefined,
        otherFields,
    };
}

/* Rows shown in Lead Info: income collapsed into one row, the rest labelled and sorted (shared with the share-text builder). */
export function getDisplayedSmartFields(
    validSmartFields: [string, string | Record<string, string>][]
): DisplayedSmartField[] {
    const { monthlyIncome, annualIncome, hasIncome, otherFields } = splitSmartFields(validSmartFields);

    const rows: DisplayedSmartField[] = [];
    if (hasIncome) {
        const { fieldLabel, value } = formatSmartField('income', formatIncome(monthlyIncome, annualIncome));
        rows.push({ key: 'income', label: fieldLabel, value });
    }

    const otherRows = otherFields.map(([key, value]) => {
        const { fieldLabel, value: formattedValue } = formatSmartField(key, value);
        return { key, label: fieldLabel, value: formattedValue };
    });

    return [...rows, ...otherRows];
}
